import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import { withSentry } from "@sentry/remix";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Toaster } from "~/components/ui/sonner";
import stylesheet from "~/tailwind.css?url";
import AppSidebar from "./components/app-sidebar";
import { SidebarProvider, SidebarInset } from "./components/ui/sidebar";
import {
  EARLY_RELEASE_EMAILS,
  isFullAdminEmail,
  isHqStaffEmail,
} from "./constants";
import { getMockAuthUser, IS_MOCK_AUTH_SERVER } from "./infra/auth/mock-auth";
import { getServerAuthUser } from "./infra/auth/server";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>許認可OCR</title>
        <Meta />
        <Links />
      </head>
      <body className="bg-muted">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}
export const ErrorBoundary = () => {
  const error = useRouteError();
  const status = (error as any)?.status;
  console.log(error);
  if (status === 404) {
    return <Error404 />;
  } else {
    // captureRemixErrorBoundaryError(error);
    return <Error500 />;
  }
};

function Error404() {
  return (
    <div className="flex items-center justify-center w-full h-screen px-4">
      <Card className="max-w-sm mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl">ページが見つかりません</CardTitle>
          <CardDescription>
            お探しのページが見つかりませんでした。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <Link to="/">
              <Button className="w-full">ページに戻る</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Error500() {
  return (
    <div className="flex items-center justify-center w-full h-screen px-4">
      <Card className="max-w-sm mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl">エラー</CardTitle>
          <CardDescription>
            予期せぬエラーが発生しました。画面を更新してください。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col w-full space-y-3">
            <Button className="w-full" onClick={() => window.location.reload()}>
              画面を更新
            </Button>
            <Link to="/">
              <Button className="w-full" variant="outline">
                トップページに戻る
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export async function loader({ request }: LoaderFunctionArgs) {
  if (IS_MOCK_AUTH_SERVER) {
    return {
      logined: true,
      user: getMockAuthUser(),
    };
  }

  const user = await getServerAuthUser(request);
  return {
    logined: !!user,
    user: user,
  };
}

function App() {
  const { logined, user } = useLoaderData<typeof loader>();
  let headerNavItems: { title: string; href: string }[] = [];
  if (logined) {
    headerNavItems = [
      {
        title: "許可証等のファイルアップロード",
        href: "/documents/new",
      },
      {
        title: "許可証等のファイル一覧",
        href: "/documents",
      },
      {
        title: "提出状況一覧",
        href: "/pharmacy_statuses",
      },
    ];

    const isHqStaff = user?.email ? isHqStaffEmail(user.email) : false;
    const isFullAdmin = user?.email ? isFullAdminEmail(user.email) : false;
    const isEarlyReleaseUser =
      user?.email && EARLY_RELEASE_EMAILS.includes(user.email);

    const showPharmacistNavItems =
      isHqStaff ||
      isEarlyReleaseUser ||
      Date.now() >= new Date("2026-05-01T00:00:00+09:00").getTime();

    if (showPharmacistNavItems) {
      // 店舗担当者のみ表示するメニュー（本部担当者は表示しない）
      if (!isHqStaff) {
        headerNavItems.push({
          title: "勤務薬剤師届出書画像アップロード",
          href: "/pharmacy-documents/new",
        });
      }

      // 本部担当者と店舗担当者の両方がアクセス可能
      headerNavItems.push({
        title: "勤務薬剤師届出書画像一覧",
        href: "/pharmacy-documents",
      });
      headerNavItems.push({
        title: "勤務薬剤師所属状況確認",
        href: "/pharmacist-employments/current",
      });
    }

    // フル本部管理者のみ表示するメニュー
    if (isFullAdmin) {
      headerNavItems.push(
        {
          title: "初期データ登録",
          href: "/pharmacist-initial-data/upload",
        },
        {
          title: "初期データ一覧",
          href: "/pharmacist-initial-data",
        },
        {
          title: "社員番号マスタ管理",
          href: "/employee-masters",
        },
      );
    }
  }
  return (
    <SidebarProvider>
      {logined && (
        <AppSidebar items={headerNavItems} user={{ email: user!.email! }} />
      )}
      <SidebarInset>
        <div className="flex-1 space-y-6 p-6 pb-16">
          <Outlet />
        </div>
      </SidebarInset>
      <Toaster />
    </SidebarProvider>
  );
}

export default withSentry(App);
